import { useState } from 'react'

import SaveIcon from '@mui/icons-material/Save'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'

import { useProgress } from '@hooks/useProgress'
import type { Entity } from '@models/Entity'

import { SelectObject } from '../../../../core/components/SelectObject'
import type { GroupedFilterOption } from '../../../../core/models/Filter'
import { StyledAlert } from '../../../../theme/components/StyledAlert'
import { StyledError } from '../../../../theme/components/StyledError'
import { useUploadBijlagenForEntity } from '../../hooks/useUploadBijlagenForEntity'
import type { AssignedBijlage } from '../../types/AssignedBijlage'
import type { UploadFile } from '../../types/UploadFile'

import { UploadDropzone } from './UploadDropzone'
import { UploadFileCard } from './UploadFileCard'

function transformInvalidCharacters(string: string) {
  return string.replace(/[^\w,\s\-_()À-ÿ.]/g, '_')
}

interface Props {
  bijlage?: AssignedBijlage
  entity: Entity
  multiple?: boolean
  assignees: GroupedFilterOption[]
  documentsoort?: string
  onClose: () => void
}

export const Upload = ({
  bijlage,
  entity,
  multiple,
  assignees,
  documentsoort,
  onClose,
}: Props) => {
  const { mutate: uploadBijlagenForEntity, error } =
    useUploadBijlagenForEntity()
  const progress = useProgress()
  const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>([])
  const uploading =
    selectedFiles.length > 0 && progress.current > -1 && !progress.error
  const errorWhileUploading =
    selectedFiles.length > 0 && progress.current >= -1 && progress.error
  const requiresReview = !!bijlage?.requiresReview
  const [assignedTo, setAssignedTo] = useState<GroupedFilterOption | null>(null)
  const [errors, setErrors] = useState({})
  const [requestReview, setRequestReview] = useState(false)
  const [opleveren, setOpleveren] = useState(false)

  const displayDocumentsoort = bijlage?.documentsoort || documentsoort
  const title = `Toevoegen bijlage${multiple ? 'n' : ''}${
    displayDocumentsoort ? ` aan ${displayDocumentsoort}` : ''
  }${bijlage?.discipline ? ` ${bijlage.discipline}` : ''}`

  const showRenameWarning = selectedFiles.some(
    (selectedFile) => selectedFile.originalName !== selectedFile.name,
  )

  const handleSubmitClick = () => {
    if (requestReview && !assignedTo?.label) {
      setErrors({ assignedToError: true })
      return
    }

    setErrors({})

    uploadBijlagenForEntity(
      {
        entity,
        selectedFiles,
        progress,
        id: bijlage?._id,
        opleveren: opleveren || requestReview,
        assignedTo: assignedTo?.label,
        documentsoort,
      },
      {
        onSuccess: () => onClose(),
      },
    )
  }

  const handleClose = () => {
    progress.clear()

    onClose()
  }

  const onFilesChange = (files) => {
    const filesWithExtraProperties: UploadFile[] = files.map((file: File) => {
      const transformedName = transformInvalidCharacters(file.name)

      return {
        file,
        omschrijving: '',
        name: transformedName,
        originalName: file.name,
      }
    })

    setSelectedFiles(filesWithExtraProperties)
    progress.set({ current: -1, error: false, total: files.length })
  }

  const onOmschrijvingChange = (fileName: string, omschrijving: string) => {
    const newSelectedFiles = selectedFiles.map((selectedFile) =>
      selectedFile.name === fileName
        ? { ...selectedFile, omschrijving }
        : selectedFile,
    )

    setSelectedFiles(newSelectedFiles)
  }

  return (
    <>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
          <StyledError error={error} sx={{ margin: 0 }} />

          <UploadDropzone onFilesChange={onFilesChange} multiple={!!multiple} />

          {selectedFiles.length > 0 &&
            selectedFiles.map((selectedFile, index) => (
              <UploadFileCard
                key={selectedFile.name}
                uploadFile={selectedFile}
                onOmschrijvingChange={onOmschrijvingChange}
                state={
                  progress.current > 0 && progress.current > index
                    ? 'done'
                    : progress.current === index && !progress.error
                      ? 'loading'
                      : progress.current === index && progress.error
                        ? 'error'
                        : undefined
                }
              />
            ))}

          {selectedFiles.length > 0 && requiresReview && (
            <FormControlLabel
              sx={{ alignSelf: 'flex-start' }}
              control={<Checkbox />}
              label={`${bijlage.documentsoort} ${
                bijlage.discipline || ''
              } direct laten beoordelen`}
              onChange={() => {
                setAssignedTo(null)
                setRequestReview(!requestReview)
                setErrors((errors) => ({
                  ...errors,
                  assignedToError: false,
                }))
              }}
            />
          )}

          {selectedFiles.length > 0 && requiresReview && requestReview && (
            <SelectObject
              options={assignees.filter((x) => x.label !== bijlage.assignedTo)}
              groupBy={(assignedTo) => assignedTo.group}
              onChange={(assignee) => {
                setErrors((errors) => ({
                  ...errors,
                  assignedToError: false,
                }))
                setAssignedTo(assignee)
              }}
              value={assignedTo}
              label="Beoordelen door"
              error={errors['assignedToError']}
            />
          )}

          {/* Als geen review nodig, geef mogelijkheid om direct op te leveren. */}
          {selectedFiles.length > 0 && !requiresReview && bijlage && (
            <FormControlLabel
              sx={{ alignSelf: 'flex-start' }}
              control={<Checkbox />}
              label={`${bijlage.documentsoort} ${
                bijlage.discipline || ''
              } direct opleveren`}
              onChange={() => {
                setOpleveren(!opleveren)
              }}
            />
          )}

          {showRenameWarning && (
            <StyledAlert title="Bestandsnaam gewijzigd" sx={{ margin: 0 }}>
              Sommige bestandsnamen zijn automatisch gewijzigd vanwege verboden
              karakters.
            </StyledAlert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color="primary" onClick={handleClose} disabled={uploading}>
          {errorWhileUploading ? 'Sluiten' : 'Annuleren'}
        </Button>

        <Button
          color="primary"
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmitClick}
          disabled={!selectedFiles.length || uploading || errorWhileUploading}
          endIcon={uploading && <CircularProgress size={20} />}
        >
          Bijlagen toevoegen
        </Button>
      </DialogActions>
    </>
  )
}
